import './LevelOfStressMap.css'
import { useRef, useEffect, type RefObject, ReactElement, Fragment, useContext } from 'react'
import mapboxgl, { ExpressionSpecification } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import LevelOfStressLegend from '../LevelOfStressLegend/LevelOfStressLegend';
import GoBostonLegend from '../GoBostonLegend/GoBostonLegend'
import { GoBostonTogglesContext } from '../../views/GoBostonLevelOfSressMap/GoBostonLevelOfStressMap';
import RoutePieChart from '../RoutePieChart/RoutePieChart';

const lastMapCenterKey = "lastMapCenter"
const lastZoomLevelKey = "lastZoomLevel"

const stressLevelFourHex = '#d31f11';
const stressLevelThreeHex = '#f47a00';
const stressLevelTwoHex = '#62c8d3';
const stressLevelOneHex = '#007191';
const footpathHex = '#62c8d3'

const lts4Expr = ['==', ["get", "lts"], 4];
const lts3Expr = ['==', ["get", "lts"], 3];
const lts2Expr = ['==', ["get", "lts"], 2];
const lts1Expr = ['==', ["get", "lts"], 1];
const footpathExpr = ['==', ["get", "highway"], "footway"];
const bothSidesCycleTrackExpr = [
  'any',
  ['all',
    ['has', 'cycleway', ['properties']],

    [
      'all',
      ['has', 'cycleway', ['properties']],
      ['has', 'leftType', ['get', 'cycleway', ['properties']]],
      ['==', [
        'get', 'leftType', [
          'get', 'cycleway', ['properties']
        ]
      ],
        'track'],
      ['has', 'cycleway', ['properties']],
      ['has', 'rightType', ['get', 'cycleway', ['properties']]],
      ['==', [
        'get', 'rightType', [
          'get', 'cycleway', ['properties']
        ]
      ], 'track'
      ]
    ]]]

type LevelOfStressMapProps = {
  routes: GeoJSON.FeatureCollection[],
  routeNames: string[],
  goBostonOverlay?: boolean
}

function LevelOfStressMap({ routes, routeNames, goBostonOverlay = false }: LevelOfStressMapProps): ReactElement {

  const { showProjects, showLevelOfStress, showExistingInfrastructure } = useContext(GoBostonTogglesContext)
  
  const filteredRoutes = routes.map(r => {
    if (!showExistingInfrastructure) {
      return {
        ...r,
        features: r.features.filter(f =>  ['future', 'priority'].includes(f.properties?.['goBoston']))
      };
    }
    return r;
  });

  const ltsPaint = {
    'line-color': [
      'case',
      lts4Expr,
      stressLevelFourHex,
      lts3Expr,
      stressLevelThreeHex,
      lts2Expr,
      stressLevelTwoHex,
      lts1Expr,
      stressLevelOneHex,
      footpathExpr,
      footpathHex,
      stressLevelFourHex
    ] as ExpressionSpecification,
    'line-opacity': goBostonOverlay ? 1 : .8,
    'line-width': 2
  };

  const goBostonExisting = ['==', ["get", "goBoston"], "existing"]
  const goBostonFuture = ['==', ["get", "goBoston"], "future"]
  const goBostonPriority = ['==', ["get", "goBoston"], "priority"]

  // const goBostonExistingHex = '#B5C1B8'
  const goBostonExistingHex = '#a0a0a0'
  const goBostonFutureHex = '#71AA88'
  const goBostonPriorityHex = '#03694B'
  const goBostonDefaultHex = '#202020'

  const goBostonPriorityPaint = {
    'line-color': [
      'case',
      goBostonExisting,
      goBostonExistingHex,
      goBostonFuture,
      goBostonFutureHex,
      goBostonPriority,
      goBostonPriorityHex,
      goBostonDefaultHex
    ] as ExpressionSpecification,
    'line-width': 8,
    'line-opacity': 1
  }

  const mapRef: RefObject<mapboxgl.Map> = useRef(null as unknown as mapboxgl.Map);
  const mapContainerRef: RefObject<HTMLDivElement> = useRef(null as unknown as HTMLDivElement);

  useEffect(() => {
    mapboxgl.accessToken = 'pk.eyJ1IjoidGxhbmdzZm9yZCIsImEiOiJjbWM4MTkzMGYxaGJxMmxwdGdweTVqb3RhIn0.S0CyG6BWDXPKNyG-mjJQOQ'
    const localStorageLastCenter = window.localStorage.getItem(lastMapCenterKey)
    const initialCenter = localStorageLastCenter ? JSON.parse(localStorageLastCenter) : { "lng": -71.09679412683583, "lat": 42.33081574894257 }
    const localStorageLastZoom = window.localStorage.getItem(lastZoomLevelKey)
    const initialZoom = localStorageLastZoom ? parseFloat(localStorageLastZoom) : 12.0
    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      center: initialCenter,
      zoom: initialZoom,
      style: "mapbox://styles/tlangsford/cmc9o4u7902f401s278a2da92",
      maxBounds: [
        [-71.39417, 42.13907],
        [-70.90290, 42.45938]
      ]
    });

    for (let i = 0; i < filteredRoutes.length; i++) {
      const route = filteredRoutes[i]
      const routeName = routeNames[i]
      mapRef.current.on('load', () => {
        mapRef.current.addSource(`${routeName}Source`, {
          type: 'geojson',
          data: route as GeoJSON.FeatureCollection
        });
      })
    }

    if (goBostonOverlay) {
      for (const routeName of routeNames) {
        mapRef.current.on('load', () => {
          mapRef.current.addLayer({
            id: routeName + ":priority",
            type: 'line',
            slot: 'middle',
            source: `${routeName}Source`,
            layout: {
              'line-join': 'bevel',
              'line-cap': 'round'
            },
            paint: goBostonPriorityPaint
          });
        })
      }
    }

    if (showLevelOfStress) {
      for (const routeName of routeNames) {
        mapRef.current.on('load', () => {
          mapRef.current.addLayer({
            id: routeName,
            type: 'line',
            source: `${routeName}Source`,
            layout: {
              'line-join': 'bevel',
              'line-cap': 'round'
            },
            paint: ltsPaint
          });
        })
      }
    }

    mapRef.current.on('mouseenter', routeNames.map(r => r + ":priority"), () => {
      mapRef.current.getCanvas().style.cursor = 'pointer'
    })
    mapRef.current.on('mouseleave', routeNames.map(r => r + ":priority"), () => {
      mapRef.current.getCanvas().style.cursor = ''
    })

    mapRef.current.on('click', routeNames.map(r => r + ":priority"), (e) => {
      if (e.features && e.features[0] && e.features[0].properties) {
        const props = e.features[0].properties;

        var html = LevelOfTrafficStressPopupHTML(props)

        new mapboxgl.Popup()
          .setLngLat(e.lngLat)
          .setHTML(html)
          .addTo(mapRef.current);
      }
    });

    mapRef.current.on('moveend', (e) => {
      window.localStorage.setItem(lastMapCenterKey, JSON.stringify(e.target.getCenter()))
    })

    mapRef.current.on('zoomend', (e) => {
      window.localStorage.setItem(lastZoomLevelKey, String(e.target.getZoom()))
    })


    return () => {
      mapRef.current.remove()
    }
  })


  return (
    <Fragment>
      <div id="root">
        { showLevelOfStress && <LevelOfStressLegend colorScale={[stressLevelOneHex, stressLevelTwoHex, stressLevelThreeHex, stressLevelFourHex]} /> }
        {goBostonOverlay && <GoBostonLegend colorScale={[goBostonExistingHex, goBostonFutureHex, goBostonPriorityHex]} />}
        {/* { showLevelOfStress && <RoutePieChart routes={routes} colorMap={{1: stressLevelOneHex, 2: stressLevelTwoHex, 3: stressLevelThreeHex, 4: stressLevelFourHex}}/> } */}
        <div id='map-container' ref={mapContainerRef} />
      </div>
    </Fragment>
  )
}

function LevelOfTrafficStressPopupHTML(properties: { [name: string]: any }) {
  let html = '<table>'
  html += '<tr>'
  html += `<td><img src="https://raw.githubusercontent.com/BostonCyclistsUnion/Website/refs/heads/main/public/Icon_LTS${properties["lts"]}.svg" width="100px"/></td>`
  html += `<td><img src="https://raw.githubusercontent.com/BostonCyclistsUnion/Website/refs/heads/main/public/Text_LTS${properties["lts"]}.svg"/></td>`
  html += '</tr>'
  html += `<tr><td>Name:</td><td>${properties["name"]}</td></tr>`
  html += `<tr><td>Type of road:</td><td>${properties["highway"]}</td></tr>`
  html += `<tr><td>Speed:</td><td>${properties["speed"]}mph</td></tr>`
  html += `<tr><td>Lanes:</td><td>${properties["lanes"]}</td></tr>`
  html += `<tr><td>Condition:</td><td>${properties["condition"]}</td></tr>`
  html += `<tr><td>Cycling facilities:</td><td>
  ${properties["cycleway"] ? 
    Object.entries(JSON.parse(properties["cycleway"])).filter(([key, _]) => key != "wayOsmId").map(([key, value]) => `${key}: ${value}`).join("<br/>") 
    : "None"
  }</td></tr>`
  html += `<tr><td>OSM ID:</td><td><a href="https://www.openstreetmap.org/way/${properties["osmId"]}">${properties["osmId"]}</a></td></tr>`
  html += '</table>'
  return html
}

export default LevelOfStressMap;